"use client"

import * as React from "react"
import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area"

import { cn } from "../../lib/utils"

type ScrollAreaScrollingContextValue = {
  scrolling: boolean
  markScrolling: () => void
  scrollbarProximity: Record<"vertical" | "horizontal", boolean>
  registerScrollbar: (orientation: "vertical" | "horizontal", element: HTMLDivElement | null) => void
}

type ScrollAreaPointerEvent = Parameters<NonNullable<ScrollAreaPrimitive.Root.Props["onPointerMove"]>>[0]

const ScrollAreaScrollingContext = React.createContext<ScrollAreaScrollingContextValue | undefined>(undefined)

const scrollbarProximityThreshold = 50

function distanceToRect(x: number, y: number, rect: DOMRect) {
  const distanceX = Math.max(rect.left - x, 0, x - rect.right)
  const distanceY = Math.max(rect.top - y, 0, y - rect.bottom)

  return Math.hypot(distanceX, distanceY)
}

/**
 * Root of a custom scroll area, for composing it from parts: place `ScrollAreaViewport` and one `ScrollBar` per
 * direction inside it. Most pages use `ScrollArea`, which includes them. It can shrink below its content, so in a
 * flex column or grid of definite height its viewport scrolls the rest. It also tracks scrolling and the pointer
 * near a scrollbar, so the scrollbar can widen and highlight its thumb.
 */
function ScrollAreaRoot({
  className,
  children,
  onWheel,
  onPointerMove,
  onPointerLeave,
  ...props
}: ScrollAreaPrimitive.Root.Props & {}) {
  const [scrolling, setScrolling] = React.useState(false)
  const [scrollbarProximity, setScrollbarProximity] = React.useState({
    vertical: false,
    horizontal: false,
  })
  const scrollingTimer = React.useRef<number | undefined>(undefined)
  const scrollbarRefs = React.useRef<Record<"vertical" | "horizontal", HTMLDivElement | null>>({
    vertical: null,
    horizontal: null,
  })

  const markScrolling = React.useCallback(() => {
    setScrolling(true)

    if (scrollingTimer.current !== undefined) {
      window.clearTimeout(scrollingTimer.current)
    }

    scrollingTimer.current = window.setTimeout(() => {
      setScrolling(false)
      scrollingTimer.current = undefined
    }, 150)
  }, [])

  React.useEffect(
    () => () => {
      if (scrollingTimer.current !== undefined) {
        window.clearTimeout(scrollingTimer.current)
      }
    },
    []
  )

  const registerScrollbar = React.useCallback(
    (orientation: "vertical" | "horizontal", element: HTMLDivElement | null) => {
      scrollbarRefs.current[orientation] = element
    },
    []
  )

  const handlePointerMove = React.useCallback(
    (event: ScrollAreaPointerEvent) => {
      if (!event.pointerType || event.pointerType === "mouse") {
        const nextProximity = {
          vertical: false,
          horizontal: false,
        }

        for (const orientation of ["vertical", "horizontal"] as const) {
          const scrollbar = scrollbarRefs.current[orientation]

          if (scrollbar) {
            nextProximity[orientation] =
              distanceToRect(event.clientX, event.clientY, scrollbar.getBoundingClientRect()) <=
              scrollbarProximityThreshold
          }
        }

        setScrollbarProximity((currentProximity) =>
          currentProximity.vertical === nextProximity.vertical &&
          currentProximity.horizontal === nextProximity.horizontal
            ? currentProximity
            : nextProximity
        )
      }

      onPointerMove?.(event)
    },
    [onPointerMove]
  )

  const handlePointerLeave = React.useCallback(
    (event: Parameters<NonNullable<ScrollAreaPrimitive.Root.Props["onPointerLeave"]>>[0]) => {
      setScrollbarProximity({ vertical: false, horizontal: false })
      onPointerLeave?.(event)
    },
    [onPointerLeave]
  )

  return (
    <ScrollAreaScrollingContext.Provider
      value={{
        scrolling,
        markScrolling,
        scrollbarProximity,
        registerScrollbar,
      }}
    >
      <ScrollAreaPrimitive.Root
        data-part="scroll-area"
        // A scroll area may always be smaller than its content: in a flex column or grid of definite height it
        // shrinks to the space it gets, and its viewport scrolls the rest.
        className={cn("relative flex min-h-0 flex-col", className)}
        {...props}
        data-scroll-area=""
        onWheel={(event) => {
          markScrolling()
          onWheel?.(event)
        }}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        {children}
      </ScrollAreaPrimitive.Root>
    </ScrollAreaScrollingContext.Provider>
  )
}

/**
 * The element that scrolls and holds the content; place it inside `ScrollAreaRoot`. It fills the root and scrolls
 * in both directions when the content is larger. The native scrollbars are hidden; `ScrollBar` draws the visible ones.
 */
const ScrollAreaViewport = React.forwardRef<HTMLDivElement, ScrollAreaPrimitive.Viewport.Props>(
  function ScrollAreaViewport({ className, children, onScroll, style, ...props }, ref) {
    const context = React.useContext(ScrollAreaScrollingContext)

    // The viewport fills the root's flex column rather than taking a percentage height, so it also fits a root
    // whose height comes from flexing inside a column that only has a max height, such as a dialog's form.
    return (
      <ScrollAreaPrimitive.Viewport
        ref={ref}
        data-part="scroll-area-viewport"
        className={cn(
          "min-h-0 w-full flex-1 rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1",
          className
        )}
        style={{ ...style, overflow: "scroll" }}
        onScroll={(event) => {
          context?.markScrolling()
          onScroll?.(event)
        }}
        {...props}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
    )
  }
)

/**
 * The way to make a region scroll; do not use `overflow` classes instead. Holds the content in a viewport with a
 * thin vertical scrollbar that widens when the pointer is near it. It has no height of its own and scrolls only when
 * it is smaller than its content, so the parent needs a definite height, such as a page, a region, a panel or a fill
 * `Stack` item, or give it a height with `className`. `PageContent scrollable`, `DialogBody` and `SheetBody`
 * already scroll; use `ScrollArea` for a separate region such as a list inside a panel. Compose
 * `ScrollAreaRoot`, `ScrollAreaViewport` and `ScrollBar` yourself for a horizontal scrollbar.
 */
function ScrollArea({
  children,
  bordered = false,
  className,
  scrollbarClassName,
  scrollbarThumbClassName,
  viewportClassName,
  ...props
}: ScrollAreaPrimitive.Root.Props & {
  /** Draws a thin rounded border around the scrolling region, for a list or a preview inside a panel. */
  bordered?: boolean
  /** Classes for the vertical scrollbar track. */
  scrollbarClassName?: string
  /** Classes for the thumb of the scrollbar. */
  scrollbarThumbClassName?: string
  /** Classes for the viewport, the element that scrolls and holds the children. */
  viewportClassName?: string
}) {
  return (
    <ScrollAreaRoot
      className={cn(bordered && "rounded-4 border border-[var(--fl-color-border-outlined-default)]", className)}
      {...props}
    >
      <ScrollAreaViewport className={viewportClassName}>{children}</ScrollAreaViewport>
      <ScrollBar className={scrollbarClassName} thumbClassName={scrollbarThumbClassName} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaRoot>
  )
}

/**
 * Root classes for the scrolling region of a popup. As a flex column with no minimum height, the region shrinks
 * when the popup is a flex column with other parts (a search field, status) and caps its height; the viewport
 * carries the max height itself, so a short list still fits its content.
 */
const popupScrollAreaStyles = "flex max-h-full min-h-0 flex-col rounded-[inherit]"

/**
 * The scrolling region of a popup: a menu's items, a picker's grid, a list in a popover. The viewport grows with
 * its content up to the popup's available height, or the max height `viewportClassName` sets, so the popup still
 * shrinks to short content. The viewport is not a tab stop: the popup's own keyboard navigation moves focus or
 * the highlight and scrolls that item into view.
 */
function PopupScrollArea({
  children,
  className,
  viewportClassName,
  ...props
}: ScrollAreaPrimitive.Root.Props & {
  /** Classes for the viewport, for example a maximum height so the area only grows up to that height. */
  viewportClassName?: string
}) {
  return (
    <ScrollAreaRoot data-part="popup-scroll-area" className={cn(popupScrollAreaStyles, className)} {...props}>
      <ScrollAreaViewport
        tabIndex={-1}
        className={cn("max-h-(--available-height) overscroll-contain", viewportClassName)}
      >
        {children}
      </ScrollAreaViewport>
      <ScrollBar />
    </ScrollAreaRoot>
  )
}

/**
 * A scrollbar with its thumb; place it inside `ScrollAreaRoot`, after the `ScrollAreaViewport`. Add one for each
 * direction the content scrolls, with `orientation` set to `vertical` (the default) or `horizontal`. It is thin; the
 * track widens when the pointer is near it, and the thumb is highlighted while the pointer is near or the content
 * scrolls.
 */
function ScrollBar({
  className,
  thumbClassName,
  orientation = "vertical",
  ...props
}: ScrollAreaPrimitive.Scrollbar.Props & {
  /** Classes for the thumb, the part the user drags. */
  thumbClassName?: string
}) {
  const context = React.useContext(ScrollAreaScrollingContext)
  const scrolling = context?.scrolling ?? false
  const nearScrollbar = context?.scrollbarProximity[orientation] ?? false
  const scrollbarRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      context?.registerScrollbar(orientation, element)
    },
    [context, orientation]
  )

  return (
    <ScrollAreaPrimitive.Scrollbar
      ref={scrollbarRef}
      data-part="scroll-area-scrollbar"
      data-orientation={orientation}
      data-scrolling={scrolling ? "" : undefined}
      data-near-scrollbar={nearScrollbar ? "" : undefined}
      orientation={orientation}
      className={cn(
        "group/scrollbar flex touch-none select-none rounded-[var(--fl-radius-4)] bg-transparent transition-[height,width,opacity] duration-normal ease-standard data-[near-scrollbar]:bg-[var(--fl-color-content-scrollbar-background)] data-[orientation=horizontal]:h-1 data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-col data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1 data-[near-scrollbar]:data-[orientation=horizontal]:h-2 data-[near-scrollbar]:data-[orientation=vertical]:w-2",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-part="scroll-area-thumb"
        className={cn(
          "relative flex-1 rounded-[var(--fl-radius-full)] bg-[var(--fl-color-content-scrollbar-default)] transition-colors duration-normal group-data-[near-scrollbar]/scrollbar:bg-[var(--fl-color-content-scrollbar-hover)] group-data-[scrolling]/scrollbar:bg-[var(--fl-color-content-scrollbar-hover)]",
          thumbClassName
        )}
      />
    </ScrollAreaPrimitive.Scrollbar>
  )
}

export { PopupScrollArea, popupScrollAreaStyles, ScrollArea, ScrollAreaRoot, ScrollAreaViewport, ScrollBar }
